import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getConsents, grantConsent, revokeConsent, getMe } from '../lib/api'
import { ShieldCheck, Trash2 } from 'lucide-react'

const ATOM_SCOPES = [
  'BiomarkerAtom', 'SleepAtom', 'HeartRateAtom', 'HRVAtom', 'ActivityAtom',
  'GaitAtom', 'SymptomAtom', 'NutrientAtom', 'MedicationAtom',
  'ClinicalObsAtom', 'GenotypeAtom', 'ContextAtom',
]
const PERMISSIONS = ['read', 'read_write', 'monitor_only']

export default function Consent() {
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [grantee, setGrantee] = useState('')
  const [selectedScopes, setSelectedScopes] = useState<string[]>([])
  const [permission, setPermission] = useState(PERMISSIONS[0])
  const [purpose, setPurpose] = useState('')
  const [error, setError] = useState('')

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => getMe().then(r => r.data) })
  const personId = me?.id

  const { data: consents = [] } = useQuery({
    queryKey: ['consents', personId],
    queryFn: () => getConsents(personId!).then((r) => r.data),
    enabled: !!personId,
  })

  const toggleScope = (s: string) =>
    setSelectedScopes(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s])

  const grant = useMutation({
    mutationFn: () => grantConsent({ grantee_did: grantee, scope: selectedScopes, permission, purpose }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consents'] })
      setShowForm(false)
      setGrantee('')
      setSelectedScopes([])
      setError('')
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(msg || 'Failed to grant consent')
    },
  })

  const revoke = useMutation({
    mutationFn: (id: string) => revokeConsent(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['consents'] }),
  })

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5 sm:mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Consent Manager</h2>
          <p className="text-sm text-gray-500">Control who can access your health data</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 self-start sm:self-auto"
        >
          <ShieldCheck size={14} /> Grant access
        </button>
      </div>

      {showForm && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">New consent</h3>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Grantee DID</label>
              <input
                type="text"
                value={grantee}
                onChange={(e) => setGrantee(e.target.value)}
                placeholder="did:familyportal:abc123…"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Atom types to share</label>
              <div className="flex flex-wrap gap-2">
                {ATOM_SCOPES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggleScope(s)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      selectedScopes.includes(s)
                        ? 'bg-brand-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {s.replace('Atom', '')}
                  </button>
                ))}
              </div>
              {selectedScopes.length === 0 && (
                <p className="text-xs text-gray-400 mt-1">Select at least one atom type</p>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Permission</label>
                <select value={permission} onChange={(e) => setPermission(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  {PERMISSIONS.map((p) => <option key={p} value={p}>{p.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Purpose</label>
                <input type="text" value={purpose} onChange={(e) => setPurpose(e.target.value)}
                  placeholder="e.g. family health sharing"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
          </div>
          {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
          <div className="flex gap-2 mt-4">
            <button onClick={() => grant.mutate()} disabled={!grantee || selectedScopes.length === 0 || grant.isPending}
              className="px-4 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 disabled:opacity-50">
              Grant
            </button>
            <button onClick={() => setShowForm(false)}
              className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm">
              Cancel
            </button>
          </div>
        </div>
      )}

      {consents.length === 0 ? (
        <p className="text-sm text-gray-400">No active consents. Grant access to a family member or clinician.</p>
      ) : (
        <div className="space-y-3">
          {consents.map((c: {
            id: string
            grantee_did: string
            scope: string[]
            permission: string
            purpose: string
            valid_until: string | null
            created_at: string
          }) => (
            <div key={c.id} className="bg-white border border-gray-200 rounded-lg p-4 flex items-start gap-3 justify-between">
              <div>
                <p className="text-sm font-medium text-gray-800 font-mono break-all">{c.grantee_did}</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(Array.isArray(c.scope) ? c.scope : [c.scope]).map((s) => (
                    <span key={s} className="text-xs bg-brand-100 text-brand-700 px-2 py-0.5 rounded">
                      {s.replace('Atom', '')}
                    </span>
                  ))}
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                    {c.permission}
                  </span>
                </div>
                {c.purpose && <p className="text-xs text-gray-400 mt-1">{c.purpose}</p>}
                <p className="text-xs text-gray-400 mt-0.5">
                  Granted {new Date(c.created_at).toLocaleDateString()}
                  {c.valid_until ? ` · Expires ${new Date(c.valid_until).toLocaleDateString()}` : ''}
                </p>
              </div>
              <button
                onClick={() => revoke.mutate(c.id)}
                className="text-gray-400 hover:text-red-500 transition-colors p-1"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
